import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

// UI
import {
  Button,
  Flex,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tag,
  Text,
} from '@chakra-ui/react'
import DatePicker from 'react-datepicker'
import NumberFormat from 'react-number-format'

import DownloadButton from '@/components/Elements/Button/DownloadButton'
import ViewFileButton from '@/components/Elements/Button/ViewFileButton'

import { TooltipCell } from '@/components/Table/components/Cell/ToolTipCell'

//Helper
import { useConfirmationModalManagement } from '@/stores/ui/confirmationModalSlice'

import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'

import { camelToWords, numberWithCommas } from '@/utils/stringHelper'

import { DateCell } from '@/components/Table/components/Cell'
import EditableTable from '@/components/Table/EditableTable'
import {
  VendorActionLogRole,
  VendorActionLogType,
  VendorActionLogTypeColor,
} from '@/features/offerCostBudgets/constants/offerCostBudget.constant'
import { useOfferCostBudget } from '@/features/offerCostBudgets/useOfferCostBudget'
import { offerSourcingGroupPolicyType } from '@/features/offers/constants/offerEditRights/offerSourcingEditRight'
import useOffer from '@/features/offers/hooks/useOffer'
import { selectOfferPermissions } from '@/features/offers/slices/offerDetail.slice'
import { hideModal, showModal } from '@/stores/ui/modalSlice'
import { MODAL_TYPES } from '@/utils/constants/modalTypes'
import { createColumnHelper } from '@tanstack/react-table'
import { toast } from 'react-toastify'

const OfferVendorDetailModal = ({
  costBudgetId,
  costBudgetIndex,
  vendorIndex,
  isCostBudgetActive = false,
  canEdit = true,
  offerId,
  projectName,
  managerId,
}) => {
  const offerPermissions = useSelector(selectOfferPermissions)

  const [vendorDetail, setVendorDetail] = useState({})
  const [editedVendorActionLogs, setEditedVendorActionLogs] = useState([])

  const { useUpdateVendorHistory, useFetchVendorDetail } = useOfferCostBudget()
  const { useUpdateOffer } = useOffer()

  const dispatch = useDispatch()
  const updateVendorHistory = useUpdateVendorHistory()
  const updateOffer = useUpdateOffer()

  // Fetch vendor detail from API
  const { data: fetchedVendorDetail } = useFetchVendorDetail({
    costBudgetId,
    vendorIndex,
    enabled: !!costBudgetId && vendorIndex !== null,
  })

  useEffect(() => {
    if (fetchedVendorDetail) {
      setVendorDetail(fetchedVendorDetail?.data || fetchedVendorDetail)
    }
  }, [fetchedVendorDetail])

  // Sync computed vendorActionLogs to editable state
  useEffect(() => {
    const actionLogs = []

    if (Array.isArray(vendorDetail?.emailLogs)) {
      vendorDetail.emailLogs.forEach((log) => {
        actionLogs.push({
          ...log,
          actionType: log.sourcingPostType,
          vendorHistoryIndex: -1,
          actionDate: log.sendDate,
          user: log.sender?.fullName,
          userRole: VendorActionLogRole.INTERNAL,
          currency: '',
          budget: 0,
          validDate: undefined,
          files: log.files,
          detail: log.comments,
          tenderDocumentIndex: log.tenderDocumentIndex,
          quotationDetails: [],
        })
      })
    }

    if (Array.isArray(vendorDetail?.history)) {
      vendorDetail.history.forEach((his, index) => {
        let detail = his.remark
        let actionType = ''

        switch (his.actionType) {
          case 'upload':
            actionType = VendorActionLogType.VENDOR_UPLOAD
            detail = 'Uploaded a new quotation'
            if (his.remark !== '') {
              detail += ', with remark:' + his.remark || '(-)'
            }
            break
          case 'decline':
            actionType = VendorActionLogType.VENDOR_DECLINE
            detail = his.remark
            break
        }

        actionLogs.push({
          ...his,
          actionType: actionType,
          vendorHistoryIndex: index,
          actionDate: his.submissionDate,
          user: his.uploader?.fullName,
          userRole: VendorActionLogRole.VENDOR,
          currency: his.currency,
          budget: his.budget,
          validDate: his.validDate,
          files: his.files,
          detail: detail,
          tenderDocumentIndex: his.tenderDocumentIndex,
          quotationDetails: his.quotationDetails || [],
        })
      })
    }
    actionLogs.sort((a, b) => new Date(a.actionDate) - new Date(b.actionDate))
    setEditedVendorActionLogs(actionLogs)
  }, [vendorDetail])

  const { open: openConfirmationModal } = useConfirmationModalManagement()

  const columnHelper = createColumnHelper()

  //Translated Variable
  // canEdit lets the caller keep the modal read-only in view mode (e.g. the [All] sub-system filter)
  const canEditVendor =
    canEdit &&
    isCostBudgetActive &&
    (offerPermissions[offerSourcingGroupPolicyType.SOURCING_COST_BUDGET]?.edit ?? false)

  const contactPersonColumnDef = useMemo(
    () => [
      columnHelper.accessor('fullName', {}),
      columnHelper.accessor('email', {}),
      columnHelper.accessor('contactNumber', {}),
    ],
    [columnHelper]
  )

  const columnDef = useMemo(
    () => [
      columnHelper.accessor('actionDate', {
        header: () => <>Date</>,
        cell: (cell) => <DateCell format="DD-MMM-YYYY HH:mm:ss" {...cell} />,
      }),
      columnHelper.accessor('user', {
        header: () => <>User</>,
        cell: ({ getValue, row: { original } }) => {
          return (
            <Flex gap="2">
              <Tag colorScheme={original?.userRole === VendorActionLogRole.INTERNAL ? 'blue' : 'yellow'}>
                {camelToWords(original?.userRole)}
              </Tag>
              <Text>{getValue()}</Text>
            </Flex>
          )
        },
      }),

      columnHelper.accessor('tenderDocumentIndex', {
        size: 200,
        header: 'Tender Version',
        cell: ({ getValue, row: { original } }) => {
          return <Text>{getValue() === 0 ? 'Tender' : `Addendum ${getValue()}`}</Text>
        },
      }),

      columnHelper.accessor('detail', {
        size: 500,
        cell: ({ getValue, row: { original } }) => {
          return (
            <Flex gap="4">
              <Tag colorScheme={VendorActionLogTypeColor[original?.actionType]}>
                {camelToWords(original?.actionType)}
              </Tag>
              <Text>{getValue()}</Text>
            </Flex>
          )
        },
      }),

      columnHelper.accessor('budget', {
        size: 200,
        cell: ({ getValue, row: { original, index }, column: { id }, table }) => {
          if (original.actionType !== VendorActionLogType.VENDOR_UPLOAD) {
            return <>-</>
          }
          if (!table.options.meta?.disabled) {
            return (
              <Flex w="100" alignItems={'center'} gap="3">
                <Text>{original?.currency}</Text>
                <Input
                  as={NumberFormat}
                  thousandSeparator
                  decimalScale={2}
                  value={getValue()}
                  disabled={false}
                  onValueChange={({ value }) => {
                    table.options.meta?.updateData(index, id, parseFloat(value) || 0)
                  }}
                />
              </Flex>
            )
          }
          return <TooltipCell text={`${original?.currency} $${numberWithCommas(getValue())}`} />
        },
      }),
      columnHelper.accessor('validDate', {
        size: 150,
        cell: ({ getValue, row: { original, index }, column: { id }, table }) => {
          const dateValue = new Date(getValue())

          // eslint-disable-next-line react-hooks/rules-of-hooks
          const [localValue, setLocalValue] = useState(getValue() ? dateValue : undefined)
          if (original.actionType !== 'vendorUpload') {
            return <>-</>
          }

          if (!table.options.meta?.disabled) {
            return (
              <Flex w="100">
                <Input
                  as={DatePicker}
                  dateFormat={'dd-MMM-yyyy'}
                  selected={localValue ? localValue : undefined}
                  disabled={false}
                  onChange={(date) => {
                    table.options.meta?.updateData(index, id, date)
                  }}
                  showYearDropdown
                  yearDropdownItemNumber={15}
                  scrollableYearDropdown
                />
              </Flex>
            )
          }
          return <TooltipCell text={getValue() ? moment(getValue()).format('DD-MMM-YYYY') : '-'} />
        },
      }),
      columnHelper.accessor('files', {
        size: 80,
        header: 'Quotation File',
        cell: ({ getValue, row: { original }, column: { id }, table }) => {
          const files = getValue() ?? []
          return (
            <Flex direction={'column'} gap="2">
              {files.map((file) => (
                <Flex gap={2} alignItems={'center'} key={file?._id}>
                  <Text mr="auto">{file?.originalFileName || ''}</Text>
                  <ViewFileButton fileId={file?._id} />
                  <DownloadButton fileId={file?._id} fileName={file?.originalFileName} />
                </Flex>
              ))}
            </Flex>
          )
        },
      }),
      columnHelper.accessor('technicalSupportDocument', {
        size: 80,
        header: 'Technical Support Doc.',
        cell: ({ getValue, row: { original }, column: { id }, table }) => {
          const files = getValue() ?? []

          return (
            <Flex direction={'column'} gap="2">
              {files.map((file) => (
                <Flex gap={2} alignItems={'center'} key={file?._id}>
                  <Text mr="auto">{file?.originalFileName || ''}</Text>
                  <ViewFileButton fileId={file?._id} />
                  <DownloadButton fileId={file?._id} fileName={file?.originalFileName} />
                </Flex>
              ))}
            </Flex>
          )
        },
      }),
      columnHelper.accessor('quotationDetails', {
        size: 80,
        header: 'Quotation Detail',
        cell: ({ getValue, row: { original }, column: { id }, table }) => {
          if (original?.actionType !== 'vendorUpload') return <></>

          return (
            <Button colorScheme="blue" onClick={() => handleOpenQuotationDetail(original)}>
              Detail
            </Button>
          )
        },
      }),
    ],
    [columnHelper]
  )

  const handleInternalUploadQuotation = async (e) => {
    e.stopPropagation()
    dispatch(
      showModal({
        modalType: MODAL_TYPES.OFFER_MANUAL_UPLOAD_VENDOR_QUOTATION,
        modalProps: {
          costBudgetId: costBudgetId,
          costBudgetIndex: costBudgetIndex,
          vendorIndex: vendorIndex,
          isCostBudgetActive: isCostBudgetActive,
        },
      })
    )
  }

  const handleOpenQuotationDetail = (history) => {
    dispatch(
      showModal({
        modalType: MODAL_TYPES.OFFER_COST_BUDGET_QUOTATION_DETAIL,
        modalProps: {
          vendorHistoryIndex: history.vendorHistoryIndex,
          offerId: offerId,
          vendorIndex: vendorIndex,
          costBudgetId: costBudgetId,
        },
      })
    )
  }

  const handleUpdateVendorHistory = (rowIndex, columnId, value) => {
    setEditedVendorActionLogs((prev) => {
      return prev.map((actionLog, index) => {
        return index === rowIndex ? { ...actionLog, [columnId]: value } : actionLog
      })
    })
  }
  const closeModal = () => {
    dispatch(hideModal())
  }

  const handleSave = async () => {
    try {
      const history = editedVendorActionLogs
        .filter(
          (log) =>
            log.actionType === VendorActionLogType.VENDOR_DECLINE ||
            log.actionType === VendorActionLogType.VENDOR_UPLOAD
        )
        .map((log) => {
          return {
            ...log,
            actionType: log.actionType === VendorActionLogType.VENDOR_UPLOAD ? 'upload' : 'decline',
            uploader: log?.uploader?._id,
          }
        })

      dispatch(showLoadingOverlay())

      await updateVendorHistory.mutateAsync({
        costBudgetId: costBudgetId,
        vendorIndex: vendorIndex,
        history: history,
      })
      closeModal()
    } catch (error) {
      console.log(error)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  if (costBudgetIndex === null || vendorIndex === null) {
    return <></>
  }
  return (
    <>
      <Modal isOpen={true} onClose={closeModal} size="xl">
        <ModalOverlay />
        <ModalContent maxW={'90%'}>
          <ModalHeader>Vendor Detail</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex w="100%" direction={'column'} gap={3}>
              <Text fontSize="lg">Vendor Contact Persons</Text>
              <EditableTable
                columnDef={contactPersonColumnDef}
                data={vendorDetail?.contactPersons ?? []}
                disabled={true}
              />
              <Text fontSize="lg">Vendor Action Log</Text>
              <Flex my="2" gap={'2'}></Flex>
              <EditableTable
                columnDef={columnDef}
                data={editedVendorActionLogs}
                disabled={!canEditVendor}
                updateData={handleUpdateVendorHistory}
                canSelect={false}
                tableWidth="100%"
                enableMultiRowSelection={false}
              />
            </Flex>
          </ModalBody>
          <ModalFooter>
            <HStack>
              {canEditVendor && (
                <Button colorScheme="green" onClick={(e) => handleInternalUploadQuotation(e)}>
                  Upload on behalf of Vendor
                </Button>
              )}
              {canEditVendor && (
                <Button colorScheme="blue" onClick={handleSave}>
                  Save
                </Button>
              )}
              <Button colorScheme="gray" onClick={closeModal}>
                Close
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default OfferVendorDetailModal
